import React, { useState } from 'react';

import { useAppDispatch } from '../hooks';

import { ClientActions } from '../actions/client/client.actions';

import type { Client } from '../interfaces/client/client.interface';

import Modal from './components/Modal';

// =============================================================================
//  Client edit modal (v3)
//
//  Edits the lightweight relationship-level fields the v3 design surfaces:
//
//    tier              — Strategic / Growth / Emerging (chip + filter)
//    description       — free-text relationship summary
//    geographic_focus  — where the client operates (Region / EMEA / etc.)
//    location          — head office city
//
//  Heavier edits (sector, category, personnel, image) stay in the legacy
//  form at /clients/:id — the v3 modal is a focused "relationship update"
//  surface, not a replacement.
// =============================================================================

const TIER_OPTIONS = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'growth', label: 'Growth' },
  { value: 'emerging', label: 'Emerging' }
] as const;

interface Props {
  client: Client;
  onClose: () => void;
}

const ClientEditModal = ({ client, onClose }: Props) => {
  const dispatch = useAppDispatch();

  const [tier, setTier] = useState<string>(client.tier || 'growth');
  const [description, setDescription] = useState<string>(client.description || '');
  const [geographic_focus, setGeographicFocus] = useState<string>(client.geographic_focus || '');
  const [location, setLocation] = useState<string>(client.location || '');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(
      ClientActions.updateClient(
        client.id as number | string,
        {
          // Re-send the name so the backend's update path doesn't trip on
          // any presence-validation when we omit fields it expects.
          name: client.name,
          tier: tier as Client['tier'],
          description,
          geographic_focus,
          location
        } as Client,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <Modal title={`Edit ${client.name || 'client'}`} onClose={onClose} footer={footer}>
      <div className="field">
        <span className="label">Tier</span>
        <select value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Description</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Relationship summary, history, key context…"
        />
      </div>

      <div className="field">
        <span className="label">Geographic focus</span>
        <input
          type="text"
          value={geographic_focus}
          onChange={(e) => setGeographicFocus(e.target.value)}
          placeholder="e.g. EMEA, Sub-Saharan Africa, South Africa"
        />
      </div>

      <div className="field">
        <span className="label">Location</span>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Head office city"
        />
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Editing sector, category, personnel, or the client logo still happens in the legacy form at{' '}
        <code>/core/clients/{client.id}</code>.
      </p>
    </Modal>
  );
};

export default ClientEditModal;
